/**
 * Mesh configuration schemas
 */

import { z } from "zod";
import { Vector3Schema } from "./scene.schema";

/**
 * Base mesh configuration
 */
const BaseMeshConfigSchema = z.object({
  checkCollisions: z.boolean().optional(),
  isVisible: z.boolean().optional(),
  material: z.string().optional(), // Reference to material by name
  name: z.string(),
  position: Vector3Schema.optional(),
  receiveShadows: z.boolean().optional(),
  rotation: Vector3Schema.optional(),
  scaling: Vector3Schema.optional(),
});

/**
 * Box mesh configuration
 */
export const BoxMeshConfigSchema = BaseMeshConfigSchema.extend({
  depth: z.number().positive().optional(),
  height: z.number().positive().optional(),
  size: z.number().positive().optional(),
  type: z.literal("box"),
  width: z.number().positive().optional(),
});

/**
 * Sphere mesh configuration
 */
export const SphereMeshConfigSchema = BaseMeshConfigSchema.extend({
  diameter: z.number().positive().optional(),
  segments: z.number().min(3).optional(),
  type: z.literal("sphere"),
});

/**
 * Ground mesh configuration
 */
export const GroundMeshConfigSchema = BaseMeshConfigSchema.extend({
  height: z.number().positive().optional(),
  subdivisions: z.number().min(1).optional(),
  type: z.literal("ground"),
  width: z.number().positive().optional(),
});

/**
 * Cylinder mesh configuration
 */
export const CylinderMeshConfigSchema = BaseMeshConfigSchema.extend({
  diameter: z.number().positive().optional(),
  diameterBottom: z.number().min(0).optional(),
  diameterTop: z.number().min(0).optional(),
  height: z.number().positive().optional(),
  tessellation: z.number().min(3).optional(),
  type: z.literal("cylinder"),
});

/**
 * Plane mesh configuration
 */
export const PlaneMeshConfigSchema = BaseMeshConfigSchema.extend({
  height: z.number().positive().optional(),
  size: z.number().positive().optional(),
  type: z.literal("plane"),
  width: z.number().positive().optional(),
});

/**
 * Torus mesh configuration
 */
export const TorusMeshConfigSchema = BaseMeshConfigSchema.extend({
  diameter: z.number().positive().optional(),
  tessellation: z.number().min(3).optional(),
  thickness: z.number().positive().optional(),
  type: z.literal("torus"),
});

/**
 * Custom mesh configuration (for imported meshes or procedural)
 */
const CustomMeshConfigSchema = BaseMeshConfigSchema.extend({
  source: z.string().optional(), // URL or identifier for custom mesh
  type: z.literal("custom"),
});

/**
 * Mesh configuration discriminated union
 */
export const MeshConfigSchema = z.discriminatedUnion("type", [
  BoxMeshConfigSchema,
  SphereMeshConfigSchema,
  GroundMeshConfigSchema,
  CylinderMeshConfigSchema,
  PlaneMeshConfigSchema,
  TorusMeshConfigSchema,
  CustomMeshConfigSchema,
]);

/**
 * Inferred types
 */
export type BoxMeshConfig = z.infer<typeof BoxMeshConfigSchema>;
export type SphereMeshConfig = z.infer<typeof SphereMeshConfigSchema>;
export type GroundMeshConfig = z.infer<typeof GroundMeshConfigSchema>;
export type CylinderMeshConfig = z.infer<typeof CylinderMeshConfigSchema>;
export type PlaneMeshConfig = z.infer<typeof PlaneMeshConfigSchema>;
export type TorusMeshConfig = z.infer<typeof TorusMeshConfigSchema>;
export type MeshConfig = z.infer<typeof MeshConfigSchema>;

/**
 * Type guards
 */
export function isBoxMeshConfig(config: MeshConfig): config is BoxMeshConfig {
  return config.type === "box";
}

export function isSphereMeshConfig(
  config: MeshConfig,
): config is SphereMeshConfig {
  return config.type === "sphere";
}

export function isGroundMeshConfig(
  config: MeshConfig,
): config is GroundMeshConfig {
  return config.type === "ground";
}

export function isCylinderMeshConfig(
  config: MeshConfig,
): config is CylinderMeshConfig {
  return config.type === "cylinder";
}

export function isPlaneMeshConfig(
  config: MeshConfig,
): config is PlaneMeshConfig {
  return config.type === "plane";
}

export function isTorusMeshConfig(
  config: MeshConfig,
): config is TorusMeshConfig {
  return config.type === "torus";
}

/**
 * Default configurations (separate from schemas as per CLAUDE.md)
 */
export const defaultBoxMeshConfig: BoxMeshConfig = {
  checkCollisions: false,
  isVisible: true,
  name: "Box",
  position: { x: 0, y: 0, z: 0 },
  receiveShadows: false,
  rotation: { x: 0, y: 0, z: 0 },
  scaling: { x: 1, y: 1, z: 1 },
  size: 1,
  type: "box",
};

export const defaultSphereMeshConfig: SphereMeshConfig = {
  checkCollisions: false,
  diameter: 2,
  isVisible: true,
  name: "Sphere",
  position: { x: 0, y: 0, z: 0 },
  receiveShadows: false,
  rotation: { x: 0, y: 0, z: 0 },
  scaling: { x: 1, y: 1, z: 1 },
  segments: 32,
  type: "sphere",
};

export const defaultGroundMeshConfig: GroundMeshConfig = {
  checkCollisions: false,
  height: 10,
  isVisible: true,
  name: "Ground",
  position: { x: 0, y: 0, z: 0 },
  receiveShadows: true,
  rotation: { x: 0, y: 0, z: 0 },
  scaling: { x: 1, y: 1, z: 1 },
  subdivisions: 1,
  type: "ground",
  width: 10,
};

export const defaultCylinderMeshConfig: CylinderMeshConfig = {
  checkCollisions: false,
  diameter: 1,
  height: 2,
  isVisible: true,
  name: "Cylinder",
  position: { x: 0, y: 0, z: 0 },
  receiveShadows: false,
  rotation: { x: 0, y: 0, z: 0 },
  scaling: { x: 1, y: 1, z: 1 },
  tessellation: 24,
  type: "cylinder",
};

export const defaultPlaneMeshConfig: PlaneMeshConfig = {
  checkCollisions: false,
  isVisible: true,
  name: "Plane",
  position: { x: 0, y: 0, z: 0 },
  receiveShadows: false,
  rotation: { x: 0, y: 0, z: 0 },
  scaling: { x: 1, y: 1, z: 1 },
  size: 1,
  type: "plane",
};

export const defaultTorusMeshConfig: TorusMeshConfig = {
  checkCollisions: false,
  diameter: 2,
  isVisible: true,
  name: "Torus",
  position: { x: 0, y: 0, z: 0 },
  receiveShadows: false,
  rotation: { x: 0, y: 0, z: 0 },
  scaling: { x: 1, y: 1, z: 1 },
  tessellation: 16,
  thickness: 0.5,
  type: "torus",
};

/**
 * Validation helpers
 */
export function validateMeshConfig(input: unknown): MeshConfig {
  return MeshConfigSchema.parse(input);
}

export function safeValidateMeshConfig(
  input: unknown,
): { success: true; data: MeshConfig } | { success: false; error: z.ZodError } {
  const result = MeshConfigSchema.safeParse(input);
  if (result.success) {
    return { data: result.data, success: true };
  }
  return { error: result.error, success: false };
}
